'use client'

import { useRef } from 'react'
import { Controller } from 'react-hook-form'
import {
  Box,
  Button,
  CloseButton,
  Dialog,
  Flex,
  HStack,
  IconButton,
  Input,
  Portal,
  Spinner,
  Text,
  Textarea,
} from '@chakra-ui/react'
import dayjs from 'dayjs'
import { Paperclip, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  DONOR_DOCUMENT_ACCEPTED_EXTENSIONS,
  DONOR_DOCUMENT_DOCTOR_MAX_LENGTH,
  DONOR_DOCUMENT_TYPES,
} from '@/entities/donor/model'
import type { TDonorDocumentType } from '@/entities/donor/model'
import { AppField } from '@/shared'
import { CustomSelect } from '@/shared/ui/select'
import { useDonorDocumentForm } from '../model/use-donor-document-form'

interface Props {
  open: boolean
  donorId: string
  onClose: () => void
}

export function AddDonorDocumentModal({ open, donorId, onClose }: Props) {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { form, isPending, onSubmit } = useDonorDocumentForm({
    donorId,
    onSuccess: onClose,
  })

  const {
    control,
    register,
    reset,
    formState: { errors },
  } = form

  const handleClose = () => {
    reset()
    onClose()
  }

  const typeOptions = DONOR_DOCUMENT_TYPES.map((type) => ({
    value: type,
    label: t(`donor_detail.documents.types.${type}`),
  }))

  return (
    <Dialog.Root
      open={open}
      onOpenChange={({ open: isOpen }) => {
        if (!isOpen) handleClose()
      }}
      size="md"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content bg="bg.surface" maxW="560px">
            <Dialog.Header pb={4}>
              <Dialog.Title fontSize="lg" fontWeight="bold">
                {t('donor_detail.documents.form.title')}
              </Dialog.Title>
              <Dialog.CloseTrigger
                asChild
                position="absolute"
                top={4}
                right={4}
              >
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Header>

            <Dialog.Body py={2}>
              <form id="donor-document-form" onSubmit={onSubmit}>
                <Flex direction="column" gap={4}>
                  <AppField.Root invalid={!!errors.date} required>
                    <AppField.Label fontSize="sm">
                      {t('donor_detail.documents.form.date_label')}
                      <AppField.RequiredIndicator />
                    </AppField.Label>
                    <Input
                      {...register('date')}
                      type="date"
                      max={dayjs().format('YYYY-MM-DD')}
                      size="sm"
                      bg="bg.subtle"
                      borderColor={errors.date ? undefined : 'border'}
                      borderRadius="md"
                    />
                    <AppField.ErrorText>
                      {errors.date?.message}
                    </AppField.ErrorText>
                  </AppField.Root>

                  <AppField.Root invalid={!!errors.type}>
                    <AppField.Label fontSize="sm">
                      {t('donor_detail.documents.form.type_label')}
                    </AppField.Label>
                    <Controller
                      control={control}
                      name="type"
                      render={({ field }) => (
                        <CustomSelect<TDonorDocumentType>
                          options={typeOptions}
                          value={field.value ? [field.value] : []}
                          onChange={(value) => field.onChange(value[0])}
                          width="100%"
                          bg="bg.subtle"
                          placeholder={t(
                            'donor_detail.documents.form.type_placeholder',
                          )}
                          clearable
                        />
                      )}
                    />
                    <AppField.ErrorText>
                      {errors.type?.message}
                    </AppField.ErrorText>
                  </AppField.Root>

                  <AppField.Root invalid={!!errors.doctor}>
                    <AppField.Label fontSize="sm">
                      {t('donor_detail.documents.form.doctor_label')}
                    </AppField.Label>
                    <Input
                      {...register('doctor')}
                      maxLength={DONOR_DOCUMENT_DOCTOR_MAX_LENGTH}
                      size="sm"
                      bg="bg.subtle"
                      placeholder={t(
                        'donor_detail.documents.form.doctor_placeholder',
                      )}
                      borderColor={errors.doctor ? undefined : 'border'}
                      borderRadius="md"
                    />
                    <AppField.ErrorText>
                      {errors.doctor?.message}
                    </AppField.ErrorText>
                  </AppField.Root>

                  <AppField.Root invalid={!!errors.note}>
                    <AppField.Label fontSize="sm">
                      {t('donor_detail.documents.form.note_label')}
                    </AppField.Label>
                    <Textarea
                      {...register('note')}
                      rows={3}
                      size="sm"
                      bg="bg.subtle"
                      borderColor={errors.note ? undefined : 'border'}
                      borderRadius="md"
                    />
                    <AppField.ErrorText>
                      {errors.note?.message}
                    </AppField.ErrorText>
                  </AppField.Root>

                  <AppField.Root invalid={!!errors.file}>
                    <AppField.Label fontSize="sm">
                      {t('donor_detail.documents.form.file_label')}
                    </AppField.Label>
                    <Controller
                      control={control}
                      name="file"
                      render={({ field }) => (
                        <Box w="100%">
                          <input
                            ref={fileInputRef}
                            type="file"
                            hidden
                            accept={DONOR_DOCUMENT_ACCEPTED_EXTENSIONS.join(
                              ',',
                            )}
                            onChange={(event) =>
                              field.onChange(event.target.files?.[0])
                            }
                          />
                          {field.value ? (
                            <HStack
                              justify="space-between"
                              gap={3}
                              px={3}
                              py={2}
                              borderWidth="1px"
                              borderColor="border"
                              borderRadius="md"
                              bg="bg.subtle"
                            >
                              <Text fontSize="sm" truncate>
                                {field.value.name}
                              </Text>
                              <IconButton
                                type="button"
                                variant="ghost"
                                size="2xs"
                                color="fg.muted"
                                _hover={{ color: 'fg' }}
                                aria-label={t(
                                  'donor_detail.documents.form.file_remove',
                                )}
                                onClick={() => {
                                  field.onChange(undefined)
                                  if (fileInputRef.current)
                                    fileInputRef.current.value = ''
                                }}
                              >
                                <X size={16} />
                              </IconButton>
                            </HStack>
                          ) : (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              borderRadius="md"
                              gap={1}
                              onClick={() => fileInputRef.current?.click()}
                            >
                              <Paperclip size={14} />
                              {t('donor_detail.documents.form.file_choose')}
                            </Button>
                          )}
                        </Box>
                      )}
                    />
                    <AppField.HelperText fontSize="xs">
                      {t('donor_detail.documents.form.file_hint')}
                    </AppField.HelperText>
                    <AppField.ErrorText>
                      {errors.file?.message}
                    </AppField.ErrorText>
                  </AppField.Root>
                </Flex>
              </form>
            </Dialog.Body>

            <Dialog.Footer pt={4} justifyContent="flex-end">
              <Button
                variant="outline"
                borderRadius="md"
                size="sm"
                px={6}
                onClick={handleClose}
              >
                {t('donor_detail.documents.form.cancel')}
              </Button>
              <Button
                type="submit"
                form="donor-document-form"
                bg="primary.solid"
                color="white"
                borderRadius="md"
                size="sm"
                px={6}
                _hover={{ opacity: 0.85 }}
                _active={{ opacity: 0.7 }}
                disabled={isPending || !donorId}
              >
                {isPending ? (
                  <Spinner size="xs" color="white" />
                ) : (
                  t('donor_detail.documents.form.submit')
                )}
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
